import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/api/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useScope, brandColorVar } from '@/context/ScopeContext';
import { StatusBadge } from '@/components/StatusBadge';
import TESTIDS from '@/constants/testIds';

function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addMonths(d, n) {
    return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function sameDay(a, b) {
    return a.toDateString() === b.toDateString();
}

export default function CalendarPage() {
    const { brandId, brands } = useScope();
    const [cursor, setCursor] = useState(startOfMonth(new Date()));
    const [scheduled, setScheduled] = useState([]);
    const [published, setPublished] = useState([]);

    const load = async () => {
        const params = {};
        if (brandId) params.brand_id = brandId;
        const [s, p] = await Promise.all([
            apiClient.get('/content/scheduled', { params }),
            apiClient.get('/content/published', { params }),
        ]);
        setScheduled(s.data);
        setPublished(p.data);
    };

    useEffect(() => {
        load();
    }, [brandId, cursor]);

    const monthDays = useMemo(() => {
        const first = startOfMonth(cursor);
        const last = endOfMonth(cursor);
        const days = [];
        const startWeekday = first.getDay();
        for (let i = 0; i < startWeekday; i++) {
            days.push(null);
        }
        for (let d = 1; d <= last.getDate(); d++) {
            days.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
        }
        return days;
    }, [cursor]);

    const eventsByDay = useMemo(() => {
        const map = new Map();
        const push = (date, ev) => {
            const key = date.toDateString();
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(ev);
        };
        for (const s of scheduled) {
            const date = new Date(s.scheduled_at);
            push(date, { type: 'scheduled', date, item: s });
        }
        for (const p of published) {
            const date = new Date(p.created_at);
            push(date, { type: 'published', date, item: p });
        }
        return map;
    }, [scheduled, published]);

    const monthLabel = cursor.toLocaleString(undefined, { month: 'long', year: 'numeric' });

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Calendar</h1>
                    <p className="text-sm text-muted-foreground">Scheduled and published posts across brands and accounts.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setCursor(addMonths(cursor, -1))} data-testid={TESTIDS.calendarPrev}>
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="min-w-[160px] text-center text-sm font-medium">{monthLabel}</div>
                    <Button size="sm" variant="ghost" onClick={() => setCursor(addMonths(cursor, 1))} data-testid={TESTIDS.calendarNext}>
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            <Card className="border-border bg-card">
                <div className="grid grid-cols-7 border-b border-border">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                        <div key={d} className="px-2 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{d}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7">
                    {monthDays.map((day, i) => {
                        const events = day ? eventsByDay.get(day.toDateString()) || [] : [];
                        return (
                            <div
                                key={i}
                                className={`min-h-[120px] border-b border-r border-border p-2 last:border-r-0 ${
                                    day && sameDay(day, new Date()) ? 'bg-[hsl(var(--surface-2))]' : ''
                                }`}
                            >
                                {day ? (
                                    <div className="mb-1 text-[11px] text-muted-foreground">{day.getDate()}</div>
                                ) : null}
                                <div className="space-y-1">
                                    {events.slice(0, 4).map((ev, idx) => {
                                        const brand = brands.find((b) => b.id === (ev.item.brand_id));
                                        const accent = brandColorVar(brand?.color_slot);
                                        return (
                                            <div
                                                key={idx}
                                                className="rounded-md border border-border bg-[hsl(var(--surface-2))] px-2 py-1 text-[11px]"
                                                style={{ borderLeftWidth: 2, borderLeftColor: `hsl(${accent})` }}
                                                data-testid={TESTIDS.calendarEventTile}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="truncate font-mono text-[10px] text-muted-foreground">
                                                        {brand?.name?.slice(0, 14) || '—'}
                                                    </span>
                                                    <StatusBadge status={ev.type === 'scheduled' ? ev.item.status : ev.item.status} className="!px-1" />
                                                </div>
                                                <div className="truncate">
                                                    {ev.date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} • {(ev.item.destination_account_ids?.length || 1)} dest
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {events.length > 4 ? (
                                        <div className="text-[10px] text-muted-foreground">+{events.length - 4} more</div>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>
        </div>
    );
}
